import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import api from '../../../services/api';

import {
  DashboardContainer,
  HeaderTitle,
  DashboardChartBox,
  DashoboardFilterBox,
  DashboardFilterContent,
  ContainerEmpty,
  DashBoardChartsContent,
  SummaryBox,
  DashBoardRespondentStatistics,
  HelpIcon,
} from './styles';

import CollapsibleSection from './AnalyticsPageComponents/CollapsibleSection';
import SurveySummaryBox from './AnalyticsPageComponents/SurveySummaryBox';
import ParticipantStatisticsTable from './AnalyticsPageComponents/ParticipantStatisticsTable';
import SurveyCompletionAnalysisTable from './AnalyticsPageComponents/SurveyCompletionAnalysisTable';
import DropoutAnalysisTable from './AnalyticsPageComponents/DropoutAnalysisTable';
import DropoutBarChart from './AnalyticsPageComponents/DropoutBarChart';
import AverageTimeBarChart from './AnalyticsPageComponents/AverageTimeBarChart';
import RawTimingDataTable from './AnalyticsPageComponents/RawTimingDataTable';
import PageTimingDetailsTable from './AnalyticsPageComponents/PageTimingDetailsTable';
import TimePerRespondentTable from './AnalyticsPageComponents/TimePerRespondentTable';
import DropoutRawDataTable from './AnalyticsPageComponents/DropoutRawDataTable';

interface Survey {
  surveyId: number;
  title: string;
}
interface TimingResponse {
  rawTimings: RawTiming[];
  metadata: {
    totalTime: number;
    totalRespondents: number;
    totalPages: number;
  };
}

interface RawTiming {
  pageNo: number;
  time: number;
  resultId: number;
}

interface PageTimingData {
  pageNo: number;
  averageTimeMs: number;
  totalResponses: number;
}

type RespondentTimingData = {
  resultId: number;
  totalTimeMs: number;
  pageCount: number;
  totalTimeFormatted: string;
};

interface State {
  project: {
    name: string;
    projectId: string;
  };
  idSurvey?: string;
}

const PageTimingAnalytics: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const hasAutoSelectedSurvey = useRef(false);
  const { projectId } = useParams();
  const location = useLocation();
  const state = location.state as State;
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [surveyId, setSurveyId] = useState<number>(0);
  const [pageTimingData, setPageTimingData] = useState<PageTimingData[]>([]);
  const [respondentTimingData, setRespondentTimingData] = useState<
    RespondentTimingData[]
  >([]);
  const [surveyAverageTime, setSurveyAverageTime] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [timingResponse, setTimingResponse] = useState<TimingResponse | null>(
    null,
  );
  const [statistics, setStatistics] = useState<any[]>([]);
  const [dropOutData, setDropOutData] = useState<any[]>([]);
  const [dropOutResults, setDropOutResults] = useState<any[]>([]);
  const [totalResults, setTotalResults] = useState<number>();
  const [totalPagesInSurvey, setTotalPagesInSurvey] = useState<number>(0);
  const [
    maxPageNumberAnsweredFinishedSurvey,
    setMaxPageNumberAnsweredFinishedSurvey,
  ] = useState<number>(0);
  const [
    totalPeopleFinishedSurveyByLastPageNumber,
    setTotalPeopleFinishedSurveyByLastPageNumber,
  ] = useState<Record<string, number>>({});
  const tooltips = [
    {
      statistic: 'Number of Links',
      text: 'Total number of survey links that have been generated.',
    },
    {
      statistic: 'Started',
      text: 'Participants who started the survey.',
    },
    {
      statistic: '≥ 30%',
      text: 'Participants who completed at least 30% of the survey.',
    },
    {
      statistic: '< 30%',
      text: 'Participants who completed less than 30% of the survey.',
    },
    {
      statistic: 'Drop-out',
      text: 'Participants who started the survey (answered at least one question) but exited before reaching the final page.',
    },
  ];
  // Helper to compute p-th percentile with interpolation
  const getPercentile = (sorted: number[], p: number): number => {
    const index = (sorted.length - 1) * p;
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
  };

  const calculateRealisticSurveyAverage = useCallback(
    (rawTimings: RawTiming[]): number => {
      // 1) group and sum total per respondent
      const totalByRespondent: Record<number, number> = {};
      rawTimings.forEach(t => {
        totalByRespondent[t.resultId] =
          (totalByRespondent[t.resultId] || 0) + t.time;
      });
      const totals = Object.values(totalByRespondent);
      if (totals.length === 0) return 0;

      // 2) general mean
      const meanAll = totals.reduce((sum, v) => sum + v, 0) / totals.length;

      // 3) IQR using interpolated percentiles
      const sorted = [...totals].sort((a, b) => a - b);
      const q1 = getPercentile(sorted, 0.25);
      const q3 = getPercentile(sorted, 0.75);
      const iqr = q3 - q1;
      const lower = q1 - 1.5 * iqr;
      const upper = q3 + 1.5 * iqr;

      // 4) filter only "realistic" totals
      const filtered = sorted.filter(v => v >= lower && v <= upper);
      if (filtered.length === 0) return meanAll; // fallback

      // 5) mean without outliers
      return filtered.reduce((sum, v) => sum + v, 0) / filtered.length;
    },
    [],
  );

  const calculatePageAverages = useCallback(
    (rawTimings: RawTiming[]): PageTimingData[] => {
      const pageGroups: Record<number, number[]> = {};

      rawTimings.forEach(timing => {
        if (!pageGroups[timing.pageNo]) {
          pageGroups[timing.pageNo] = [];
        }
        pageGroups[timing.pageNo].push(timing.time);
      });

      return Object.entries(pageGroups)
        .map(([pageNo, times]) => {
          const sum = times.reduce((total, time) => total + time, 0);
          return {
            pageNo: parseInt(pageNo, 10),
            averageTimeMs: sum / times.length,
            totalResponses: times.length,
          };
        })
        .sort((a, b) => a.pageNo - b.pageNo);
    },
    [],
  );

  const calculateRespondentTotals = useCallback(
    (rawTimings: RawTiming[]): RespondentTimingData[] => {
      const respondentGroups: Record<number, RawTiming[]> = {};

      rawTimings.forEach(timing => {
        if (!respondentGroups[timing.resultId]) {
          respondentGroups[timing.resultId] = [];
        }
        respondentGroups[timing.resultId].push(timing);
      });

      return Object.entries(respondentGroups)
        .map(([resultId, timings]) => {
          const totalTimeMs = timings.reduce(
            (total, timing) => total + timing.time,
            0,
          );
          const uniquePageCount = new Set(timings.map(t => t.pageNo)).size;
          const totalSec = Math.floor(totalTimeMs / 1000);
          const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
          const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
          const s = String(totalSec % 60).padStart(2, '0');
          const totalTimeFormatted = `${h}:${m}:${s}`;

          return {
            resultId: parseInt(resultId, 10),
            totalTimeMs,
            pageCount: uniquePageCount,
            totalTimeFormatted,
          };
        })
        .sort((a, b) => b.totalTimeMs - a.totalTimeMs);
    },
    [],
  );

  const handleSelectSurveyChange = useCallback(
    (option: any) => {
      const value = !Number.isNaN(option.value) ? option?.value : 0;
      setSurveyId(value);

      if (value) {
        setLoading(true);

        api
          .post(`/results/survey/resultsWithProgress`, { surveyId: value })
          .then((response: any) => {
            const stats: any[] = [
              {
                name: 'Number of Links',
                count: response.data.total,
              },
              { name: 'Started', count: response.data.started },
              { name: '≥ 30%', count: response.data.completed },
              {
                name: '< 30%',
                count: response.data.notCompleted,
              },
              {
                name: 'Drop-out',
                count: response.data.dropOut,
              },
            ];
            setTotalResults(response.data.total);
            setStatistics(stats);
            setDropOutData(response.data.dropOutList);
            setDropOutResults(response.data.dropOutResults);
            setTotalPagesInSurvey(response.data.totalPagesInSurvey);
            // setSurveyAverageTime(response.data.surveyCompletionTimingAvarge);
            setMaxPageNumberAnsweredFinishedSurvey(
              response.data.maxCurrentPageNoAnseredSurveyFinished,
            );
            setTotalPeopleFinishedSurveyByLastPageNumber(
              response.data.totalPeopleFinishedSurveyByLastPageNumber || {},
            );
          });

        api
          .get(`/results/raw-page-timing-data/${value}`)
          .then(response => {
            const data = response.data as TimingResponse;
            setTimingResponse(data);
            const pageAverages = calculatePageAverages(data.rawTimings);
            setPageTimingData(pageAverages);
            const respondentTotals = calculateRespondentTotals(data.rawTimings);
            setRespondentTimingData(respondentTotals);

            const realisticMean = calculateRealisticSurveyAverage(
              data.rawTimings,
            );
            setSurveyAverageTime(realisticMean);
          })
          .catch(error => {
            console.error('Error loading page timing data:', error);
            setPageTimingData([]);
            setRespondentTimingData([]);
            setTimingResponse(null);
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        setPageTimingData([]);
        setRespondentTimingData([]);
        setTimingResponse(null);
      }
    },
    [
      calculatePageAverages,
      calculateRealisticSurveyAverage,
      calculateRespondentTotals,
    ],
  );

  const formatTime = (ms: number): string => {
    if (!ms) return '0s';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${seconds}s`;
  };

  useEffect(() => {
    if (
      !hasAutoSelectedSurvey.current &&
      state?.idSurvey &&
      surveys.length > 0
    ) {
      hasAutoSelectedSurvey.current = true;

      const found = surveys.find(s => s.surveyId === Number(state.idSurvey));

      if (found) {
        setSurveyId(found.surveyId);
        handleSelectSurveyChange({ value: found.surveyId });
      }
    }
  }, [surveys, state?.idSurvey, handleSelectSurveyChange]);

  useEffect(() => {
    if (projectId) {
      setLoading(true);
      api
        .get(`/surveys/project/${projectId}`)
        .then(response => {
          setSurveys(response.data);
        })
        .catch(error => {
          console.error('Error loading surveys:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [projectId]);

  return (
    <>
      <h1>Page Timing Analytics</h1>

      <DashboardContainer>
        <DashboardFilterContent>
          <DashoboardFilterBox>
            <Form ref={formRef} onSubmit={() => {}}>
              <div>
                <p>Select Survey</p>
                <select
                  value={surveyId || ''}
                  onChange={e =>
                    handleSelectSurveyChange({
                      value: parseInt(e.target.value, 10),
                    })
                  }
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '8px',
                    marginBottom: '10px',
                  }}
                >
                  <option value="">Choose a survey...</option>
                  {surveys.map(survey => (
                    <option key={survey.surveyId} value={survey.surveyId}>
                      {survey.title} - {survey.surveyId}
                    </option>
                  ))}
                </select>
              </div>
            </Form>
          </DashoboardFilterBox>
        </DashboardFilterContent>

        {!loading ? (
          <>
            {surveyId !== 0 && (
              <>
                <SurveySummaryBox
                  surveyId={surveyId}
                  surveyAverageTime={surveyAverageTime}
                  totalResults={totalResults}
                  formatTime={formatTime}
                />

                <DashBoardRespondentStatistics>
                  <ParticipantStatisticsTable
                    statistics={statistics}
                    tooltips={tooltips}
                  />
                  <SurveyCompletionAnalysisTable
                    totalPeopleFinishedSurveyByLastPageNumber={
                      totalPeopleFinishedSurveyByLastPageNumber
                    }
                  />
                  <DropoutAnalysisTable dropOutData={dropOutData} />
                </DashBoardRespondentStatistics>

                <DashBoardChartsContent>
                  <DropoutBarChart dropOutData={dropOutData} />
                </DashBoardChartsContent>
              </>
            )}
            {surveyId !== 0 && pageTimingData.length > 0 && (
              <>
                <DashBoardChartsContent>
                  <AverageTimeBarChart
                    pageTimingData={pageTimingData}
                    formatTime={formatTime}
                  />
                </DashBoardChartsContent>

                <CollapsibleSection title="Drop-out Details - Raw Data">
                  <DropoutRawDataTable dropOutResults={dropOutResults} />
                </CollapsibleSection>

                <CollapsibleSection title="Page Timing Details - Raw Data">
                  <PageTimingDetailsTable
                    pageTimingData={pageTimingData}
                    formatTime={formatTime}
                  />
                </CollapsibleSection>

                <CollapsibleSection title="Time Per Respondent - Raw Data">
                  <TimePerRespondentTable
                    respondentTimingData={respondentTimingData}
                    formatTime={formatTime}
                  />
                </CollapsibleSection>

                <CollapsibleSection title="Timing Data for all Respondents - Raw Data">
                  <RawTimingDataTable
                    rawTimings={timingResponse?.rawTimings || []}
                    formatTime={formatTime}
                  />
                </CollapsibleSection>
              </>
            )}
            {surveyId === 0 ? (
              <ContainerEmpty>
                {'Please select a survey to view page timing analytics.'}
              </ContainerEmpty>
            ) : (
              <ContainerEmpty>
                <br />
              </ContainerEmpty>
            )}
          </>
        ) : (
          <ContainerEmpty>
            <div className="loading-spinner"></div>
            <p>Loading data...</p>
          </ContainerEmpty>
        )}
      </DashboardContainer>
    </>
  );
};

export default PageTimingAnalytics;
